import React from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Heading,
  Button,
  Box,
  Flex,
  Input,
  Select,
  Badge,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Text,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import dayjs from 'dayjs';
import xhr from 'xhr';

const STATUS_MAP = {
  paid: { label: 'Pagado', color: 'green' },
  pending: { label: 'Pendiente', color: 'yellow' },
  cancelled: { label: 'Cancelado', color: 'red' },
};

const PAYMENT_METHODS = [
  'Transferencia bancaria',
  'Efectivo',
  'Nequi',
  'Daviplata',
  'Consignacion',
  'Otro',
];

export default function SchoolPaymentsTab({ schoolId }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [payments, setPayments] = React.useState([]);
  const [students, setStudents] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [filterStudent, setFilterStudent] = React.useState('');

  const [newPayment, setNewPayment] = React.useState({
    student: '',
    amount: '',
    payment_date: dayjs().format('YYYY-MM-DD'),
    payment_method: '',
    description: '',
    status: 'paid',
  });

  React.useEffect(() => {
    if (!schoolId) return;
    fetchPayments();
    fetchStudents();
  }, [schoolId]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const res = await xhr(
        `/payment-schools?filters[school][id][$eq]=${schoolId}&populate=student,program_type&sort[0]=payment_date:desc&pagination[pageSize]=100`
      );
      setPayments(res.data.data || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const res = await xhr(
        `/students?filters[schoolFrom][id][$eq]=${schoolId}&fields[0]=id&populate=user&pagination[pageSize]=500`
      );
      const data = res.data.data || [];
      setStudents(
        data.map((s) => {
          const attrs = s.attributes || s;
          const studentUser = attrs.user?.data?.attributes || attrs.user;
          return {
            id: s.id,
            name:
              `${studentUser?.first_name || ''} ${studentUser?.last_name || ''}`.trim() ||
              `Estudiante ${s.id}`,
            email: studentUser?.email || '',
          };
        })
      );
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleCreate = async () => {
    if (!newPayment.student || !newPayment.amount || !newPayment.payment_method) {
      toast({
        title: 'Campos requeridos',
        description: 'Estudiante, monto y metodo de pago son obligatorios',
        status: 'warning',
        position: 'bottom',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      setCreating(true);
      await xhr.post('/payment-schools', {
        data: {
          student: Number(newPayment.student),
          amount: Number(newPayment.amount),
          payment_date: newPayment.payment_date,
          payment_method: newPayment.payment_method,
          description: newPayment.description,
          status: newPayment.status,
          school: schoolId,
        },
      });

      toast({
        title: 'Pago registrado exitosamente',
        status: 'success',
        position: 'bottom',
        duration: 3000,
        isClosable: true,
      });
      setNewPayment({
        student: '',
        amount: '',
        payment_date: dayjs().format('YYYY-MM-DD'),
        payment_method: '',
        description: '',
        status: 'paid',
      });
      onClose();
      fetchPayments();
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Error al registrar pago';
      toast({
        title: 'Error al registrar pago',
        description: message,
        status: 'error',
        position: 'bottom',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const filteredPayments = filterStudent
    ? payments.filter((p) => {
        const attrs = p.attributes || p;
        const student = attrs.student?.data || attrs.student;
        return student?.id === Number(filterStudent);
      })
    : payments;

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading size="sm">Pagos Registrados</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="purple" size="sm" onClick={onOpen}>
          Registrar Pago
        </Button>
      </Flex>

      <Flex mb={4} gap={3} alignItems="center">
        <Select
          placeholder="Filtrar por estudiante"
          value={filterStudent}
          onChange={(e) => setFilterStudent(e.target.value)}
          maxW="400px"
          bg="white"
          size="sm"
        >
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.email})
            </option>
          ))}
        </Select>
        {filterStudent && (
          <Button variant="ghost" size="xs" onClick={() => setFilterStudent('')}>
            Limpiar
          </Button>
        )}
      </Flex>

      <TableContainer bg="white" borderRadius="lg" boxShadow="sm">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Fecha</Th>
              <Th>Estudiante</Th>
              <Th isNumeric>Monto</Th>
              <Th>Metodo</Th>
              <Th>Descripcion</Th>
              <Th>Estado</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredPayments.map((p) => {
              const attrs = p.attributes || p;
              const student = attrs.student?.data?.attributes || attrs.student;
              const studentUser = student?.user?.data?.attributes || student?.user;
              const statusInfo = STATUS_MAP[attrs.status] || STATUS_MAP.paid;

              return (
                <Tr key={p.id}>
                  <Td>
                    {attrs.payment_date ? dayjs(attrs.payment_date).format('DD/MM/YYYY') : '-'}
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {studentUser?.first_name || ''} {studentUser?.last_name || ''}
                    </Text>
                  </Td>
                  <Td isNumeric fontWeight="medium">
                    ${Number(attrs.amount || 0).toLocaleString()}
                  </Td>
                  <Td>{attrs.payment_method || '-'}</Td>
                  <Td>
                    <Text fontSize="sm" noOfLines={1} maxW="200px">
                      {attrs.description || '-'}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={statusInfo.color}>{statusInfo.label}</Badge>
                  </Td>
                </Tr>
              );
            })}
            {filteredPayments.length === 0 && (
              <Tr>
                <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                  No hay pagos registrados
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Modal: Registrar Pago */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Registrar Pago</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4}>
              <FormControl isRequired>
                <FormLabel>Estudiante</FormLabel>
                <Select
                  placeholder="Seleccionar estudiante"
                  value={newPayment.student}
                  onChange={(e) => setNewPayment((p) => ({ ...p, student: e.target.value }))}
                >
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.email})
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Flex gap={4}>
                <FormControl isRequired flex="1">
                  <FormLabel>Monto</FormLabel>
                  <Input
                    type="number"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment((p) => ({ ...p, amount: e.target.value }))}
                    placeholder="0"
                  />
                </FormControl>
                <FormControl isRequired flex="1">
                  <FormLabel>Fecha de pago</FormLabel>
                  <Input
                    type="date"
                    value={newPayment.payment_date}
                    onChange={(e) =>
                      setNewPayment((p) => ({ ...p, payment_date: e.target.value }))
                    }
                  />
                </FormControl>
              </Flex>

              <Flex gap={4}>
                <FormControl isRequired flex="1">
                  <FormLabel>Metodo de pago</FormLabel>
                  <Select
                    placeholder="Seleccionar metodo"
                    value={newPayment.payment_method}
                    onChange={(e) =>
                      setNewPayment((p) => ({ ...p, payment_method: e.target.value }))
                    }
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl flex="1">
                  <FormLabel>Estado</FormLabel>
                  <Select
                    value={newPayment.status}
                    onChange={(e) => setNewPayment((p) => ({ ...p, status: e.target.value }))}
                  >
                    <option value="paid">Pagado</option>
                    <option value="pending">Pendiente</option>
                  </Select>
                </FormControl>
              </Flex>

              <FormControl>
                <FormLabel>Descripcion</FormLabel>
                <Textarea
                  value={newPayment.description}
                  onChange={(e) => setNewPayment((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Concepto del pago, mes, observaciones..."
                  rows={2}
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="purple" onClick={handleCreate} isLoading={creating}>
              Registrar Pago
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
